import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid
} from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import { Visit } from '../types';
import '../chartSetup';

interface EngagementAnalyticsProps {
  visits: Visit[];
}

export const EngagementAnalytics: React.FC<EngagementAnalyticsProps> = ({ visits }) => {
  /* -------------------- DATA PROCESSING -------------------- */

  const outcomeData = useMemo(() => {
    const counts: Record<string, number> = {};
    visits.forEach(v => {
      counts[v.outcome] = (counts[v.outcome] || 0) + 1;
    });
    return counts;
  }, [visits]);

  const visitTypeData = useMemo(() => {
    const counts: Record<string, number> = {};
    visits.forEach(v => {
      counts[v.type] = (counts[v.type] || 0) + 1;
    });
    return counts;
  }, [visits]);

  const productData = useMemo(() => {
    const counts: Record<string, number> = {};
    visits.forEach(v => {
      counts[v.product] = (counts[v.product] || 0) + 1;
    });
    return counts;
  }, [visits]);

  /* -------------------- COLORS -------------------- */

  const COLORS = ['#ef4444', '#f59e0b', '#8b5cf6', '#10b981', '#3b82f6', '#ec4899'];

  const outcomeColors: Record<string, string> = {
    'Very Positive': '#4caf50',
    'Committed to Try': '#2196f3',
    Positive: '#2196f3',
    Neutral: '#ff9800',
    'Needs Follow-up': '#f44336',
  };

  /* -------------------- CHART CONFIGS -------------------- */

  const outcomeChart = {
    labels: Object.keys(outcomeData),
    datasets: [
      {
        label: 'Visits',
        data: Object.values(outcomeData),
        backgroundColor: Object.keys(outcomeData).map(
          o => outcomeColors[o] || COLORS[0]
        ),
        borderRadius: 8,
      },
    ],
  };

  const visitTypeChart = {
    labels: Object.keys(visitTypeData),
    datasets: [
      {
        label: 'Visits',
        data: Object.values(visitTypeData),
        backgroundColor: COLORS,
        borderRadius: 8,
      },
    ],
  };

  const productChart = {
    labels: Object.keys(productData),
    datasets: [
      {
        data: Object.values(productData),
        backgroundColor: COLORS,
      },
    ],
  };

  /* -------------------- RENDER -------------------- */

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Engagement Analytics
      </Typography>

      <Grid container spacing={2}>
        {/* Outcome Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                Outcome Distribution
              </Typography>
              <Bar
                data={outcomeChart}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Visit Type */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                Visit Type
              </Typography>
              <Bar
                data={visitTypeChart}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Product Distribution */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                Product Distribution
              </Typography>
              <Box height={300}>
                <Pie
                  data={productChart}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
